import React, { useEffect } from 'react';
import styled from 'styled-components';
import qs from 'qs';

import { Button, Alert } from 'components/bootstrap';
import getDisplayName from 'authentication/helpers/getDisplayName';
import { AUTH_SERVICE_TYPE, SSO_PATH_PREFIX } from 'authentication/components/saml/constants';
import { prefixUrl } from 'routing/Routes';
import { LOGIN_INITIALIZED_STATE } from 'logic/authentication/constants';
import { currentPathnameWithoutPrefix } from 'util/URLUtils';

const StyledA = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 1em;
  display: block;
  cursor: pointer;
  width: 100%;
`;

type SAMLFailureCause = {
  message: string;
  status_code?: number;
};

const parseError = (encodedError: string): SAMLFailureCause | undefined => {
  if (!encodedError) {
    return undefined;
  }

  try {
    return JSON.parse(decodeURIComponent(atob(encodedError))) as SAMLFailureCause;
  } catch (e) {
    return { message: e.message };
  }
};

const SAMLLoginForm = ({ setLoginFormState }) => {
  const { _saml_login_error_: loginError, ...otherParams } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const { message, status_code: statusCode } = parseError(loginError as string) || {};
  const statusMessage = statusCode ? ` (Status: ${statusCode})` : '';

  const displayName = getDisplayName(AUTH_SERVICE_TYPE);
  // We don't want to carry the "_saml_login_error_" params over to the SAML login URL
  const newSearch = qs.stringify(otherParams);
  const currentRelativeUrl = `${currentPathnameWithoutPrefix()}${newSearch && `?${newSearch}`}`;
  const queryParams = qs.stringify({ relayState: encodeURIComponent(currentRelativeUrl) });
  const loginUrl = prefixUrl(`${SSO_PATH_PREFIX}/saml/login?${queryParams}`);

  useEffect(() => setLoginFormState(LOGIN_INITIALIZED_STATE), [setLoginFormState]);

  return (
    <>
      {message && (
        <Alert bsStyle="danger" title={`SAML Login Error${statusMessage}`}>
          {message}
        </Alert>
      )}
      <StyledA href={loginUrl}>
        <StyledButton bsStyle="info">{`Login with ${displayName}`}</StyledButton>
      </StyledA>
    </>
  );
};

export default SAMLLoginForm;
